import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import {
  AppStatus,
  Loading,
  LoanStatus,
  Modal,
  SectionHeading,
  SectionPreTitle,
  SectionSubTitle,
  SectionTitle,
  SectionWrapper,
  getStatusText
} from '../atoms';
import {
  FormattedCurrency,
  FormattedDate,
  FormattedMobile
} from '../../common/utils/formatters';
import { getMambuLoanDetails } from '../../contexts';
import { SimpleTable, SimpleTableRow } from '../molecules';
import { CancelApplication } from './forms/CancelApplication';
import type { TApplication } from '../types';
// import { EditCourseEndDate } from './EditCourseEndDate';

const StyledReasonList = styled.ul`
  margin: 0;
  margin-bottom: 1em;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

const StyledReason = styled.li`
  list-style: none;
  height: 1em;
  line-height: 1em;
  margin: 0;
  padding: 0;
`;

type TApplicationModal = {
  modalTitle?: string;
  content: TApplication;
  isModalOpen: boolean;
  handleModalClose: () => void;
  refreshPage: () => void;
};

const ApplicationModal = ({
  modalTitle,
  content,
  isModalOpen,
  handleModalClose,
  refreshPage
}: TApplicationModal) => {
  const {
    applicationStep,
    applicationSubStep,
    arrears,
    borrower,
    creationDate,
    declineReasons = [],
    downgradeReasons = [],
    daysPastDue,
    email,
    firstPaymentDate,
    frequency,
    fundingToDate,
    loanApplicationId,
    loanApplicationOriginalId,
    loanStatus,
    mambuAccountId,
    pasDowngrade,
    paymentAmount,
    paymentsToDate,
    phoneNumber,
    planAmount,
    planBalance,
    pol,
    polSubCategory,
    postCode,
    salesforceAppNumber,
    salesPerson,
    student,
    termInMonths
  } = content;

  const displayReasons = declineReasons?.length
    ? declineReasons
    : downgradeReasons;
  const [reasons, setReasons] = useState(displayReasons);
  const [loanLoading, setLoanLoading] = useState(false);

  // load (mapped) mambu outcome reasons
  useEffect(() => {
    if (isModalOpen && mambuAccountId) {
      (async () => {
        try {
          setLoanLoading(true);
          const details = await getMambuLoanDetails(mambuAccountId);
          if (details?.keyOutcomeReason) {
            setReasons([
              ...new Set([...displayReasons, details?.keyOutcomeReason])
            ]);
          }
        } catch (e) {
          console.error(`Failed to load loan '${mambuAccountId}'`, e);
          toast.error(
            'Something went wrong while trying to retrieve loan details. Please refresh and try again.'
          );
        } finally {
          setLoanLoading(false);
        }
      })();
    }
  }, [isModalOpen, mambuAccountId, displayReasons]);

  const statusText = getStatusText(applicationStep, applicationSubStep);
  return (
    <Modal
      handleClose={handleModalClose}
      isModalVisible={isModalOpen}
      content={
        <>
          {modalTitle && <SectionPreTitle>{modalTitle}</SectionPreTitle>}
          <SectionTitle>
            {salesforceAppNumber || loanApplicationId}
          </SectionTitle>
          <SectionSubTitle>
            Created:&nbsp;
            <FormattedDate date={creationDate} />
          </SectionSubTitle>
          <div>
            <SectionWrapper>
              <SectionHeading>Basic Details</SectionHeading>
              <SimpleTable>
                <SimpleTableRow
                  title="Borrower Name"
                  contentType="text"
                  content={borrower || ''}
                />
                <SimpleTableRow
                  title="Student Name"
                  contentType="text"
                  content={student || ''}
                />
                <SimpleTableRow
                  title="Email"
                  contentType="text"
                  content={email || ''}
                  breakAll
                />
                <SimpleTableRow
                  title="Mobile Phone"
                  contentType="text"
                  content={
                    phoneNumber ? <FormattedMobile mobile={phoneNumber} /> : ''
                  }
                />
                {postCode && (
                  <SimpleTableRow
                    title="Postal Code"
                    contentType="text"
                    content={postCode}
                  />
                )}
              </SimpleTable>
            </SectionWrapper>
            <SectionWrapper>
              <SectionHeading>Application Details</SectionHeading>
              <SimpleTable>
                <SimpleTableRow
                  title="Status"
                  contentType="text"
                  content={<AppStatus statusText={statusText} small={true} />}
                />
                {pasDowngrade && (
                  <SimpleTableRow
                    title="Converted to Payment Assist"
                    contentType="text"
                    content="Yes"
                  />
                )}
                {reasons?.length || loanLoading ? (
                  <SimpleTableRow
                    title="Reason"
                    contentType="text"
                    content={
                      <StyledReasonList>
                        {reasons?.map((reason) => (
                          <StyledReason key={reason}>{reason}</StyledReason>
                        ))}
                        {loanLoading && (
                          <StyledReason key="loading">
                            <Loading small />
                          </StyledReason>
                        )}
                      </StyledReasonList>
                    }
                  />
                ) : undefined}
                {salesPerson && (
                  <SimpleTableRow
                    title="Sales Agent"
                    contentType="text"
                    content={salesPerson}
                  />
                )}

                <SimpleTableRow
                  title="Course Name"
                  contentType="text"
                  content={pol || ''}
                />
                <SimpleTableRow
                  title="Selected Sub Course"
                  contentType="text"
                  content={
                    polSubCategory !== 'NO_DATA' ? polSubCategory : 'None'
                  }
                />
                {/* {(applicationStep === 'Approved' ||
                  applicationStep === 'Signed') && (
                  <EditCourseEndDate application={content} />
                )} */}
              </SimpleTable>
            </SectionWrapper>

            {(firstPaymentDate ||
              planAmount !== 'NO_DATA' ||
              loanStatus ||
              fundingToDate ||
              paymentsToDate ||
              paymentAmount ||
              frequency ||
              planBalance ||
              arrears ||
              daysPastDue ||
              pasDowngrade) && (
              <SectionWrapper>
                <SectionHeading>Plan Details</SectionHeading>
                <SimpleTable>
                  {mambuAccountId && (
                    <SimpleTableRow
                      title="Mambu ID"
                      contentType="text"
                      content={mambuAccountId}
                    />
                  )}
                  {loanStatus && (
                    <SimpleTableRow
                      title="Status"
                      contentType="text"
                      content={
                        <LoanStatus statusText={loanStatus} small={true} />
                      }
                    />
                  )}
                  {(mambuAccountId || loanStatus || pasDowngrade) && (
                    <SimpleTableRow title="" contentType="hr" />
                  )}
                  {firstPaymentDate && (
                    <SimpleTableRow
                      title="First Payment Date"
                      contentType="text"
                      content={<FormattedDate date={firstPaymentDate} />}
                    />
                  )}
                  {planAmount !== 'NO_DATA' && (
                    <SimpleTableRow
                      title="Plan Amount"
                      contentType="text"
                      content={<FormattedCurrency amount={planAmount} />}
                    />
                  )}
                  {fundingToDate && (
                    <SimpleTableRow
                      title="Funding to date"
                      contentType="text"
                      content={<FormattedCurrency amount={fundingToDate} />}
                    />
                  )}
                  {paymentsToDate && (
                    <SimpleTableRow
                      title="Payments to date"
                      contentType="text"
                      content={<FormattedCurrency amount={paymentsToDate} />}
                    />
                  )}
                  {planBalance && (
                    <SimpleTableRow
                      title="Plan Balance"
                      contentType="text"
                      content={<FormattedCurrency amount={planBalance} />}
                    />
                  )}
                  {paymentAmount && (
                    <SimpleTableRow
                      title="Payment Amount"
                      contentType="text"
                      content={<FormattedCurrency amount={paymentAmount} />}
                    />
                  )}
                  {frequency && (
                    <SimpleTableRow
                      title="Frequency"
                      contentType="text"
                      content={frequency}
                    />
                  )}
                  {termInMonths && (
                    <SimpleTableRow
                      title="Term (in Months)"
                      contentType="text"
                      content={termInMonths}
                    />
                  )}
                  {arrears && (
                    <>
                      <SimpleTableRow title="" contentType="hr" />
                      <SimpleTableRow
                        title="Delinquent Amount"
                        contentType="text"
                        content={<FormattedCurrency amount={arrears} />}
                      />
                    </>
                  )}
                  {daysPastDue && (
                    <SimpleTableRow
                      title="Days Past Due"
                      contentType="text"
                      content={daysPastDue}
                    />
                  )}
                </SimpleTable>
              </SectionWrapper>
            )}
            {applicationStep === 'NEW - Entered' && (
              <SectionWrapper>
                <CancelApplication
                  loanApplicationOriginalId={loanApplicationOriginalId}
                  handleModalClose={handleModalClose}
                  refreshPage={refreshPage}
                />
              </SectionWrapper>
            )}
          </div>
        </>
      }
    />
  );
};

export { ApplicationModal };
